/**
 * Runtime configuration service that reads environment-specific configuration
 * from window._env_ object injected at container startup
 */

export interface AppConfig {
  apiBaseUrl: string;
  wsBaseUrl: string;
  environment: string;
}

// Default configuration (fallback)
const defaultConfig: AppConfig = {
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL || 'http://localhost:5253',
  wsBaseUrl: import.meta.env.VITE_WS_BASE_URL || 'http://localhost:5253',
  environment: import.meta.env.VITE_ENVIRONMENT || 'development'
};

// Runtime configuration injected by container
declare global {
  interface Window {
    _env_?: {
      REACT_APP_API_BASE_URL?: string;
      REACT_APP_WS_BASE_URL?: string;
      REACT_APP_ENVIRONMENT?: string;
    };
  }
}

/**
 * Get runtime configuration with fallback to build-time configuration
 */
export function getConfig(): AppConfig {
  // Check if runtime configuration is available
  const runtimeConfig = window._env_;
  
  if (runtimeConfig) {
    return {
      apiBaseUrl: runtimeConfig.REACT_APP_API_BASE_URL || defaultConfig.apiBaseUrl,
      wsBaseUrl: runtimeConfig.REACT_APP_WS_BASE_URL || defaultConfig.wsBaseUrl,
      environment: runtimeConfig.REACT_APP_ENVIRONMENT || defaultConfig.environment
    };
  }
  
  // Fallback to build-time configuration
  return defaultConfig;
}

/**
 * Get API base URL for the current environment
 */
export function getApiBaseUrl(): string {
  return getConfig().apiBaseUrl;
}

/**
 * Get WebSocket base URL for the current environment
 */
export function getWsBaseUrl(): string {
  return getConfig().wsBaseUrl;
}

/**
 * Get current environment name
 */
export function getEnvironment(): string {
  return getConfig().environment;
}

/**
 * Check if running in development mode
 */
export function isDevelopment(): boolean {
  return getEnvironment() === 'development';
}

/**
 * Check if running in QA mode
 */
export function isQA(): boolean {
  return getEnvironment() === 'qa';
}

/**
 * Check if running in production mode
 */
export function isProduction(): boolean {
  return getEnvironment() === 'production';
}